/**
 * CEX control-plane WebSocket — shared [wsHub] connection with refcounted topics.
 * Auto-reconnect; exposes `bot.*`, `risk.*`, etc. (see CEX /api/ws).
 */

import { useEffect, useState } from 'react'
import { cexWebSocketUrl } from '../lib/apiBase.js'
import { getWsHub } from '../lib/wsHub.js'

/**
 * @param {object} [opts]
 * @param {string[]} [opts.subjects] optional NATS-style subjects; if empty, no per-topic subscribe frame is sent
 * @param {boolean} [opts.trackEvents] when true (default), buffer recent WS messages in state (for BotMonitor). When false, only `connected` is tracked.
 */
export function useCexWebSocket({ subjects = [], trackEvents = true } = {}) {
  const [events, setEvents] = useState([])
  const [connected, setConnected] = useState(false)
  const subKey = Array.isArray(subjects) ? subjects.join('\0') : ''

  useEffect(() => {
    const hub = getWsHub()
    const url = cexWebSocketUrl()
    hub.connect(url)
    const offStatus = hub.subscribeConnection(setConnected)
    const unsubs = []

    if (trackEvents) {
      const effectSubjects = subKey ? subKey.split('\0').filter(Boolean) : []
      if (effectSubjects.length) {
        for (const subj of effectSubjects) {
          unsubs.push(
            hub.subscribe(subj, (msg) => {
              setEvents((prev) => {
                const next = [...prev, msg]
                return next.length > 500 ? next.slice(-500) : next
              })
            }),
          )
        }
      } else {
        unsubs.push(
          hub.subscribeAllMessages((msg) => {
            setEvents((prev) => {
              const next = [...prev, msg]
              return next.length > 500 ? next.slice(-500) : next
            })
          }),
        )
      }
    }

    return () => {
      offStatus()
      for (const u of unsubs) u()
    }
  }, [subKey, trackEvents])

  return { events, connected }
}
